import ChevronDoubleLeftIcon from '@heroicons/react/24/outline/ChevronDoubleLeftIcon';
import ChevronDoubleRightIcon from '@heroicons/react/24/outline/ChevronDoubleRightIcon';
import ChevronLeftIcon from '@heroicons/react/24/outline/ChevronLeftIcon';
import ChevronRightIcon from '@heroicons/react/24/outline/ChevronRightIcon';
import React, { useEffect,useState } from 'react';

import { ButtonPanel } from '@/components/core/ButtonPanel';
import { LOCAL_STORAGE } from '@/constants';
import { useTranslation } from '@/hooks/useTranslation';

import { InputNumber } from './components/InputNumber/InputNumber';

export type PaginationNavigation = 'full' | 'compact';

export interface PaginationPageProps {
    totalItems: number;
    currentPage?: number;
    onPageChange?: (page: number) => void;
    /**
     * `full` (default): first/last page buttons + editable InputNumber for the page.
     * `compact`: previous/next only; current page is shown as plain text (no InputNumber).
     */
    navigation?: PaginationNavigation;
};

const PaginationPage = React.forwardRef<HTMLDivElement, PaginationPageProps> (({
    totalItems,
    currentPage,
    onPageChange,
    navigation = 'full',
}, ref) => {
    const { t } = useTranslation();
    const [internalCurrentPage, setInternalCurrentPage] = useState(1);
    const [inputValue, setInputValue] = useState<string>(String(''));
    const [currentLanguage, setCurrentLanguage] = useState<string>('');

    const isCompact = navigation === 'compact';
    const actualCurrentPage = currentPage ?? internalCurrentPage;
    const setCurrentPage = onPageChange ?? setInternalCurrentPage;

    const itemsPerPage = 10;

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const startIndex = (actualCurrentPage - 1) * itemsPerPage + 1;
    const endIndex = Math.min(actualCurrentPage * itemsPerPage, totalItems);
    const isFirstPage = actualCurrentPage === 1;
    const isLastPage = actualCurrentPage === totalPages;

    useEffect(() => {
        setInputValue(String(actualCurrentPage));
    }, [actualCurrentPage]);

    useEffect(() => {
        const languageKey = localStorage.getItem(LOCAL_STORAGE.LANGUAGE_KEY);
        setCurrentLanguage(languageKey || '');
    }, []);

    const handleInputChange = (val: string) => {
        setInputValue(val);
    };

    const handleInputBlur = () => {
        let parsed = Number(inputValue);
        if (isNaN(parsed) || parsed < 1) {
            parsed = 1;
        } else if (totalPages > 0 && parsed > totalPages) {
            parsed = totalPages;
        } else if (totalPages === 0) {
            parsed = 1; // Handle case when there are no items
        }

        setInputValue(String(parsed));
        setCurrentPage(parsed);
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleInputBlur(); // Apply clamping on Enter key
            e.currentTarget.blur(); // Remove focus after Enter
        }
    };

    // Locale-aware thousands separators (e.g. 1.000 de / 1,000 en), same as
    // elsewhere in the app. Used only for display text — InputNumber stays plain.
    const formatCount = (value: number) =>
        value.toLocaleString(currentLanguage ? currentLanguage.replace(/_/g, '-') : undefined);

    return (
        <div
            ref={ref}
            className="tw-flex tw-px-2 tw-justify-between tw-items-center tw-self-stretch sm:tw-justify-center md:tw-justify-between lg:tw-justify-between">
            <div className="tw-items-center tw-gap-1 tw-text-t-primary tw-text-right tw-text-t-sm lg-font-dynamic tw-hidden md:tw-flex lg:tw-flex xl:tw-flex">
                <p className='tw-m-0'>{t('SHOWING')}</p>
                <p className='tw-m-0'>{formatCount(startIndex)} &ndash; {formatCount(endIndex)}</p>
                <p className='tw-m-0'>{t('OF').toLowerCase()}</p>
                <p className='tw-m-0'>{formatCount(totalItems)}</p>
                <p className='tw-m-0'>{currentLanguage === 'de' ? t('PUBLICATIONS.ITEMS') :t('PUBLICATIONS.ITEMS').toLowerCase()}</p>
            </div>
            <div className="tw-flex tw-justify-between tw-items-center tw-gap-2">
                <div className='tw-flex !tw-gap-1'>
                    {!isCompact && (
                        <ButtonPanel
                            isDisabled={isFirstPage}
                            icon={ChevronDoubleLeftIcon}
                            tooltip={t('GO_TO_FIRST_PAGE')}
                            onClick={() => setCurrentPage(1)}
                        />
                    )}
                    <ButtonPanel
                        isDisabled={isFirstPage}
                        icon={ChevronLeftIcon}
                        tooltip={t('GO_TO_PREVIOUS_PAGE')}
                        onClick={() => setCurrentPage(Math.max(1, actualCurrentPage - 1))}
                    />
                </div>
                <div className='tw-flex tw-justify-center tw-items-center tw-gap-1 tw-text-t-sm lg-font-dynamic tw-w-40 lg:tw-w-44 tw-text-t-primary'>
                    {isCompact ? (
                        <p className='tw-m-0'>{formatCount(actualCurrentPage)}</p>
                    ) : (
                        <InputNumber
                            //add the logic when the input will be disabled
                            isDisabled={false}
                            value={inputValue}
                            onChange={(val: string | number) => handleInputChange(String(val))}
                            onBlur={handleInputBlur}
                            onKeyDown={handleInputKeyDown}
                        />
                    )}
                    <p className='tw-m-0'> {t('OF').toLowerCase()}</p>
                    <p className='tw-m-0'> {formatCount(totalPages)} </p>
                    <p className='tw-m-0'> {t('PAGES')} </p>
                </div>

                <div className='tw-flex !tw-gap-1'>
                    <ButtonPanel
                        isDisabled={isLastPage}
                        icon={ChevronRightIcon}
                        tooltip={t('GO_TO_NEXT_PAGE')}
                        onClick={() => setCurrentPage(Math.min(totalPages, actualCurrentPage + 1))}/>
                    {!isCompact && (
                        <ButtonPanel
                            isDisabled={isLastPage}
                            icon={ChevronDoubleRightIcon}
                            tooltip={t('GO_TO_LAST_PAGE')}
                            onClick={() => setCurrentPage(totalPages)}
                        />
                    )}
                </div>
            </div>
        </div>
    );
});

PaginationPage.displayName = 'PaginationPage';

export { PaginationPage };
